import { defineStore } from 'pinia';
import { uselocalStorageGetterSetter } from '@/composables/uselocalStorageGetterSetter';
import { useUserDetailsStore } from '@/stores/useUserDetails';
import { onboardingCategories, onboardingTopicContentTypes } from '@/constants/onboardingChecklist';

/** completed/total tally over `itemIds`, plus the derived percent complete. */
const progressOf = (itemIds, itemCompletion) => {
  const completed = itemIds.filter((itemId) => itemCompletion[itemId]).length;
  const total = itemIds.length;
  return { completed, total, percent: total === 0 ? 0 : Math.round((completed / total) * 100) };
};

const ITEM_COMPLETION_STORAGE_KEY = 'GET_STARTED_ITEM_COMPLETION';
const DONT_SHOW_AGAIN_STORAGE_KEY = 'GET_STARTED_DONT_SHOW_AGAIN';

/** Reads and parses a key, falling back rather than throwing on missing or malformed JSON. */
const readWithFallback = (getParsedObject, key, fallback) => {
  try {
    return getParsedObject(key) || fallback;
  } catch {
    return fallback;
  }
};

export const useGetStartedStore = defineStore('getStartedStore', {
  state: () => {
    return {
      // Maps a checklist item id to `true` once it has been completed in this browser.
      // e.g. { 'staging-a-job': true }
      itemCompletion: {},
      // Permanent, per-browser dismissal — once set, the widget never auto-shows again.
      dontShowAgain: false,
      // Was the checklist already finished when this page loaded? `null` until the first
      // `hydrate()`. Snapshot rather than live percent so finishing the last item keeps the
      // widget mounted long enough to show its completion screen — see `shouldShowWidget`.
      wasCompleteAtLoad: null,
    };
  },
  actions: {
    hydrate() {
      const { getParsedObject } = uselocalStorageGetterSetter();

      this.itemCompletion = readWithFallback(getParsedObject, ITEM_COMPLETION_STORAGE_KEY, {});
      this.dontShowAgain = readWithFallback(getParsedObject, DONT_SHOW_AGAIN_STORAGE_KEY, false);

      // First hydrate only. This runs on every route resolution, but the store itself is only
      // rebuilt by a real page load, so guarding on `null` makes this a per-load snapshot —
      // an in-app navigation (including the one a completing walkthrough triggers) can't
      // retroactively hide a widget the carrier just finished.
      if (this.wasCompleteAtLoad === null) {
        this.wasCompleteAtLoad = this.overallProgress.percent === 100;
      }
    },
    persistItemCompletion() {
      const { setObject } = uselocalStorageGetterSetter();
      setObject(ITEM_COMPLETION_STORAGE_KEY, this.itemCompletion);
    },
    persistDontShowAgain() {
      const { setObject } = uselocalStorageGetterSetter();
      setObject(DONT_SHOW_AGAIN_STORAGE_KEY, this.dontShowAgain);
    },
    /**
     * Mark a single checklist item complete and persist immediately. Idempotent: calling it
     * again for an already-complete item is a no-op, matching `useFeatureAnnouncementsStore`'s
     * `markSeen` precedent. Callers (a walkthrough's real `onComplete`) never un-complete an
     * item, so there's no companion "uncomplete" action.
     */
    markItemComplete(itemId) {
      if (this.itemCompletion[itemId]) {
        return;
      }
      this.itemCompletion = { ...this.itemCompletion, [itemId]: true };
      this.persistItemCompletion();

      const { trackEvent, eventNames } = useAcvAnalytics();
      trackEvent(eventNames.GETSTARTED_TOPIC_COMPLETED, {
        topic_id: itemId,
        content_type: onboardingTopicContentTypes[itemId],
      });

      if (this.overallProgress.percent === 100) {
        useUserDetailsStore().completeOnboardingWalkthrough();
      }
    },
    /** Permanently hide the widget for this browser, independent of `hasUserOnboarded` send to BE. */
    dismissPermanently() {
      this.dontShowAgain = true;
      this.persistDontShowAgain();
      useUserDetailsStore().completeOnboardingWalkthrough();
    },
  },
  getters: {
    /** Categories the current user's role may see; one without `allowedRoles` is open to all. */
    visibleCategories() {
      const role = useUserDetailsStore().role?.toLowerCase();
      return onboardingCategories.filter(
        (category) => !category.allowedRoles || category.allowedRoles.includes(role),
      );
    },
    /**
     * All item ids across every visible category — the single source `overallProgress`
     * counts against, so the total is never a hardcoded number.
     */
    allItemIds() {
      return this.visibleCategories.flatMap((category) => category.topicIds);
    },
    /** `{ completed, total, percent }` across every authored item. */
    overallProgress() {
      return progressOf(this.allItemIds, this.itemCompletion);
    },
    /** `{ completed, total, percent }` for one category, or all-zero for an unknown id. */
    categoryProgress() {
      return (categoryId) => {
        const category = onboardingCategories.find((c) => c.id === categoryId);
        return progressOf(category?.topicIds ?? [], this.itemCompletion);
      };
    },
    /** Has every topic in this category been completed? False for an unknown category id. */
    isCategoryComplete() {
      return (categoryId) => this.categoryProgress(categoryId).percent === 100;
    },
    /** Has this checklist item been completed? False for an unknown/uncompleted item id. */
    isItemComplete() {
      return (itemId) => Boolean(this.itemCompletion[itemId]);
    },
    /** BE-owned onboarded state. Falls back to `false` until the user details fetch resolves. */
    hasUserOnboarded() {
      return useUserDetailsStore().user?.hasCompletedOnboardingWalkthrough ?? false;
    },
    /**
     * The resolved OR-gate (ADR 0003): hidden once any one of the three signals fires, so no
     * consumer needs to know or combine them itself.
     *
     * The completion signal is `wasCompleteAtLoad`, not the live percent: reading the percent
     * here made reaching 100% unmount the widget in the same tick the last item completed,
     * so `GetStarted/CompletionScreen.vue` could never render. Finishing the checklist now
     * leaves the widget up showing that screen, and the next page load hides it.
     */
    shouldShowWidget() {
      return !(this.hasUserOnboarded || this.wasCompleteAtLoad || this.dontShowAgain);
    },
  },
});
